import { formatarDataHora as quando } from '@common/i18n/formatosData';
import { Alert, Badge, Button, IconButton, Popover, ProgressBar } from '@ntconsult/aluma-react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useConvites } from '../hooks';

const TONE = { em_andamento: 'info' } as const;

/**
 * A última rodada de convites (spec 29 R6); nunca houve rodada, não aparece. Terminada, vira o
 * "Último envio" (spec 34 R15): uma frase, sem selo nem detalhes; `onFalhas` filtra as falhas.
 */
export function RodadaConvites({
  campanhaId,
  onFalhas,
  modo,
}: {
  campanhaId: string;
  onFalhas?: () => void;
  /**
   * No painel: `info` é o ícone do cartão "Convidados", só com a rodada concluída (o "com falha"
   * já está no cartão); `faixa` é a linha acima da lista, só em andamento ou interrompida. Sem
   * `modo`, tudo como faixa (Início).
   */
  modo?: 'info' | 'faixa';
}) {
  const { t } = useTranslation('convites');
  const rodada = useConvites(campanhaId).data?.ultimaRodada;
  if (!rodada) return null;
  const concluida = rodada.status === 'concluida';
  if ((modo === 'info' && !concluida) || (modo === 'faixa' && concluida)) return null;
  if (rodada.status === 'em_andamento') {
    const feitos = rodada.enviados + rodada.falhas;
    return (
      <section className="flex flex-col gap-2 text-sm" data-rodada>
        <Badge tone={TONE.em_andamento} className="self-start">
          {t('rodada.status.em_andamento')}
        </Badge>
        <ProgressBar
          className="max-w-xl"
          value={rodada.total > 0 ? Math.round((100 * feitos) / rodada.total) : 100}
          label={t('rodada.barra', {
            processados: t('rodada.processados', { feitos, count: rodada.total }),
            enviados: t('rodada.enviados', { count: rodada.enviados }),
            falhas: rodada.falhas,
          })}
        />
      </section>
    );
  }
  const em = rodada.concluidaEm ? quando(rodada.concluidaEm) : '';
  const secao = (
    <section
      className={`flex flex-col gap-1 text-sm ${modo === 'info' ? 'max-w-sm' : ''}`}
      data-rodada
      data-status-rodada={rodada.status}
    >
      <h3 className="text-sm font-semibold text-foreground">{t('ultimoEnvio.titulo')}</h3>
      {rodada.status === 'interrompida' ? (
        <Alert
          variant="warning"
          title={t('ultimoEnvio.interrompido', {
            quando: em,
            enviados: rodada.enviados,
            total: rodada.total,
          })}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span data-ultimo-envio>
            {t('ultimoEnvio.concluido', {
              count: rodada.enviados,
              quando: em,
              nome: rodada.acionadaPor.nome,
            })}
          </span>
          {rodada.falhas >= 1 && onFalhas && modo !== 'info' && (
            <Button variant="ghost" size="sm" onClick={onFalhas}>
              {t('ultimoEnvio.falhas', { count: rodada.falhas })}
            </Button>
          )}
        </div>
      )}
    </section>
  );
  if (modo !== 'info') return secao;
  return (
    <Popover
      placement="bottom-end"
      trigger={<IconButton size="sm" icon={<Info size={16} />} label={t('ultimoEnvio.titulo')} />}
    >
      {secao}
    </Popover>
  );
}
